import {Emitter} from './emitter';
import {TrackEvent, type VideoCodec} from './enums';
import {Mutex} from './lock';
import type {AudioCaptureOptions, TrackProcessor, VideoCaptureOptions} from './options';

let trackCounter = 0;
const nextSid = (prefix: string) => `${prefix}_${Date.now().toString(36)}${(trackCounter++).toString(36)}`;

export abstract class Track extends Emitter<string> {
	readonly kind: Track.Kind;
	source: Track.Source = Track.Source.Unknown;
	sid?: Track.SID;
	attachedElements: Array<HTMLMediaElement> = [];
	isMuted = false;
	streamState: Track.StreamState = Track.StreamState.Active;
	protected _mediaStreamTrack: MediaStreamTrack;
	#mediaStream?: MediaStream;

	protected constructor(mediaTrack: MediaStreamTrack, kind: Track.Kind) {
		super();
		this.kind = kind;
		this._mediaStreamTrack = mediaTrack;
	}

	get mediaStreamTrack(): MediaStreamTrack {
		return this._mediaStreamTrack;
	}

	get mediaStreamID(): string {
		return this._mediaStreamTrack.id;
	}

	get mediaStream(): MediaStream {
		if (!this.#mediaStream || this.#mediaStream.getTracks()[0] !== this.playbackTrack) {
			this.#mediaStream = new MediaStream([this.playbackTrack]);
		}
		return this.#mediaStream;
	}

	/** The track shown or played locally (processed if a processor runs). */
	protected get playbackTrack(): MediaStreamTrack {
		return this._mediaStreamTrack;
	}

	get dimensions(): Track.Dimensions | undefined {
		if (this.kind !== Track.Kind.Video) return undefined;
		const {width, height} = this.playbackTrack.getSettings();
		return width && height ? {width, height} : undefined;
	}

	attach(): HTMLMediaElement;
	attach(element: HTMLMediaElement): HTMLMediaElement;
	attach(element?: HTMLMediaElement): HTMLMediaElement {
		const el = element ?? document.createElement(this.kind === Track.Kind.Video ? 'video' : 'audio');
		if (!this.attachedElements.includes(el)) this.attachedElements.push(el);
		this.bindElement(el);
		this.emit(TrackEvent.ElementAttached, el);
		return el;
	}

	protected bindElement(el: HTMLMediaElement): void {
		if (el.srcObject !== this.mediaStream) el.srcObject = this.mediaStream;
		el.autoplay = true;
		if (el instanceof HTMLVideoElement) {
			el.playsInline = true;
			el.muted = true;
			el.addEventListener('playing', () => this.emit(TrackEvent.VideoPlaybackStarted), {once: true});
		}
		el.play().catch((error: unknown) => {
			this.emit(
				this.kind === Track.Kind.Video ? TrackEvent.VideoPlaybackFailed : TrackEvent.AudioPlaybackFailed,
				error,
			);
		});
	}

	protected rebindElements(): void {
		for (const el of this.attachedElements) this.bindElement(el);
	}

	detach(): Array<HTMLMediaElement>;
	detach(element: HTMLMediaElement): HTMLMediaElement;
	detach(element?: HTMLMediaElement): HTMLMediaElement | Array<HTMLMediaElement> {
		const detachOne = (el: HTMLMediaElement) => {
			if (el.srcObject === this.#mediaStream) el.srcObject = null;
			this.attachedElements = this.attachedElements.filter((e) => e !== el);
			this.emit(TrackEvent.ElementDetached, el);
			return el;
		};
		if (element) return detachOne(element);
		return [...this.attachedElements].map(detachOne);
	}

	stop(): void {
		this._mediaStreamTrack.stop();
	}

	protected setMutedState(muted: boolean): void {
		if (this.isMuted === muted) return;
		this.isMuted = muted;
		this.emit(muted ? TrackEvent.Muted : TrackEvent.Unmuted, this);
	}
}

export namespace Track {
	export enum Kind {
		Audio = 'audio',
		Video = 'video',
		Unknown = 'unknown',
	}
	export type SID = string;
	export enum Source {
		Camera = 'camera',
		Microphone = 'microphone',
		ScreenShare = 'screen_share',
		ScreenShareAudio = 'screen_share_audio',
		Unknown = 'unknown',
	}
	export enum StreamState {
		Active = 'active',
		Paused = 'paused',
		Unknown = 'unknown',
	}
	export interface Dimensions {
		width: number;
		height: number;
	}
}

type Processor = TrackProcessor<any, any>;

/** Callbacks the MoQ publisher installs on a published local track. */
export interface LocalTrackSink {
	trackChanged(track: MediaStreamTrack): void;
	muteChanged(muted: boolean): void;
}

export abstract class LocalTrack extends Track {
	/** No WebRTC sender exists in parley; tuning code checks it optionally. */
	readonly sender: RTCRtpSender | undefined = undefined;
	protected providedByUser: boolean;
	constraints: MediaTrackConstraints;
	protected processor?: Processor;
	protected processorElement?: HTMLMediaElement;
	protected stagedReplacementTrack?: MediaStreamTrack;
	#lock = new Mutex();
	#sink?: LocalTrackSink;
	#audioContext?: AudioContext;

	protected constructor(
		mediaTrack: MediaStreamTrack,
		kind: Track.Kind,
		constraints?: MediaTrackConstraints,
		userProvided = false,
	) {
		super(mediaTrack, kind);
		this.constraints = constraints ?? {};
		this.providedByUser = userProvided;
		this.sid = nextSid('TR');
		this.watchEnded(mediaTrack);
	}

	get isUserProvided(): boolean {
		return this.providedByUser;
	}

	get isLocal(): true {
		return true;
	}

	get audioContext(): AudioContext | undefined {
		return this.#audioContext;
	}

	setAudioContext(ctx: AudioContext | undefined): void {
		this.#audioContext = ctx;
	}

	/** The track actually sent: the processor's output when one runs. */
	get effectiveTrack(): MediaStreamTrack {
		return this.processor?.processedTrack ?? this._mediaStreamTrack;
	}

	protected override get playbackTrack(): MediaStreamTrack {
		return this.effectiveTrack;
	}

	/** @internal Connect this track to the MoQ publisher. */
	bindSink(sink: LocalTrackSink | undefined): void {
		this.#sink = sink;
		if (sink) {
			sink.trackChanged(this.effectiveTrack);
			sink.muteChanged(this.isMuted);
		}
	}

	protected notifyTrackChanged(): void {
		this.#sink?.trackChanged(this.effectiveTrack);
		this.rebindElements();
	}

	#handleEnded = () => {
		this.emit(TrackEvent.Ended, this);
	};

	protected watchEnded(track: MediaStreamTrack): void {
		track.addEventListener('ended', this.#handleEnded);
	}

	async mute(): Promise<this> {
		this._mediaStreamTrack.enabled = false;
		this.setMutedState(true);
		this.#sink?.muteChanged(true);
		return this;
	}

	async unmute(): Promise<this> {
		this._mediaStreamTrack.enabled = true;
		this.setMutedState(false);
		this.#sink?.muteChanged(false);
		return this;
	}

	async runWithTrackChangeLock<T>(operation: () => Promise<T>): Promise<T> {
		const unlock = await this.#lock.lock();
		try {
			return await operation();
		} finally {
			unlock();
		}
	}

	protected async setMediaStreamTrack(track: MediaStreamTrack, stopPrevious: boolean): Promise<void> {
		const previous = this._mediaStreamTrack;
		if (previous === track) return;
		previous.removeEventListener('ended', this.#handleEnded);
		this._mediaStreamTrack = track;
		track.enabled = !this.isMuted;
		if (this.processor) {
			await this.processor.restart(this.processorOptions());
		}
		if (stopPrevious && !this.stagedReplacementTrack) previous.stop();
		this.notifyTrackChanged();
	}

	async replaceTrack(
		track: MediaStreamTrack,
		userProvidedOrOptions?: boolean | {userProvidedTrack?: boolean; stopProcessor?: boolean},
	): Promise<this> {
		const userProvided =
			typeof userProvidedOrOptions === 'boolean'
				? userProvidedOrOptions
				: (userProvidedOrOptions?.userProvidedTrack ?? true);
		await this.runWithTrackChangeLock(async () => {
			this.providedByUser = userProvided;
			await this.setMediaStreamTrack(track, true);
			this.watchEnded(track);
		});
		return this;
	}

	async stageTrackReplacement(track: MediaStreamTrack): Promise<this> {
		await this.runWithTrackChangeLock(async () => {
			if (track === this._mediaStreamTrack)
				throw new Error('unable to stage the active source track as its own replacement');
			if (track.readyState !== 'live') throw new Error('unable to stage an ended replacement track');
			this.providedByUser = true;
			await this.setMediaStreamTrack(track, false);
			this.stagedReplacementTrack = track;
		});
		return this;
	}

	async commitStagedTrackReplacement(track: MediaStreamTrack, userProvidedTrack: boolean): Promise<this> {
		await this.runWithTrackChangeLock(async () => {
			if (this.stagedReplacementTrack !== track || this._mediaStreamTrack !== track) {
				throw new Error('unable to commit a replacement that is not the active staged track');
			}
			if (track.readyState !== 'live') throw new Error('unable to commit an ended staged track');
			this.watchEnded(track);
			this.providedByUser = userProvidedTrack;
			this.stagedReplacementTrack = undefined;
		});
		return this;
	}

	async restartTrack(options?: AudioCaptureOptions | VideoCaptureOptions): Promise<this> {
		const constraints = {...this.constraints, ...(options ? captureConstraints(options) : {})} as MediaTrackConstraints;
		const stream = await navigator.mediaDevices.getUserMedia(
			this.kind === Track.Kind.Audio ? {audio: constraints} : {video: constraints},
		);
		const [track] = this.kind === Track.Kind.Audio ? stream.getAudioTracks() : stream.getVideoTracks();
		this.constraints = constraints;
		await this.replaceTrack(track, false);
		this.emit(TrackEvent.Restarted, this);
		return this;
	}

	protected processorOptions(): {
		kind: Track.Kind;
		track: MediaStreamTrack;
		element?: HTMLMediaElement;
		audioContext?: AudioContext;
		localTrack: LocalTrack;
	} {
		return {
			kind: this.kind,
			track: this._mediaStreamTrack,
			element: this.processorElement,
			audioContext: this.#audioContext,
			localTrack: this,
		};
	}

	getProcessor(): Processor | undefined {
		return this.processor;
	}

	async setProcessor(processor: Processor, showProcessedStreamLocally = true): Promise<void> {
		await this.runWithTrackChangeLock(async () => {
			if (this.processor) await this.#destroyProcessor();
			if (this.kind === Track.Kind.Audio && !this.#audioContext) this.#audioContext = new AudioContext();
			if (this.kind === Track.Kind.Video && showProcessedStreamLocally) {
				this.processorElement = document.createElement('video');
				this.processorElement.muted = true;
			}
			await processor.init(this.processorOptions());
			this.processor = processor;
			this.notifyTrackChanged();
			this.emit(TrackEvent.TrackProcessorUpdate, processor);
		});
	}

	async #destroyProcessor(): Promise<void> {
		const processor = this.processor;
		this.processor = undefined;
		this.processorElement = undefined;
		await processor?.destroy();
	}

	async stopProcessor(keepElement = true): Promise<void> {
		void keepElement;
		await this.runWithTrackChangeLock(async () => {
			if (!this.processor) return;
			await this.#destroyProcessor();
			this.notifyTrackChanged();
			this.emit(TrackEvent.TrackProcessorUpdate);
		});
	}

	async stopProcessorIfCurrent(processor: Processor, keepElement = true): Promise<boolean> {
		if (this.processor !== processor) return false;
		await this.stopProcessor(keepElement);
		return true;
	}

	override stop(): void {
		void this.processor?.destroy();
		this.processor = undefined;
		super.stop();
	}
}

export function captureConstraints(options: AudioCaptureOptions | VideoCaptureOptions): MediaTrackConstraints {
	const {processor: _processor, ...rest} = options as AudioCaptureOptions & VideoCaptureOptions;
	const out: MediaTrackConstraints = {...(rest as MediaTrackConstraints)};
	const resolution = (options as VideoCaptureOptions).resolution;
	if (resolution) {
		delete (out as Record<string, unknown>).resolution;
		out.width = {ideal: resolution.width};
		out.height = {ideal: resolution.height};
		if (resolution.frameRate) out.frameRate = {ideal: resolution.frameRate};
	}
	return out;
}

export class LocalAudioTrack extends LocalTrack {
	constructor(
		mediaTrack: MediaStreamTrack,
		constraints?: MediaTrackConstraints,
		userProvided = true,
		audioContext?: AudioContext,
	) {
		super(mediaTrack, Track.Kind.Audio, constraints, userProvided);
		this.source = Track.Source.Microphone;
		this.setAudioContext(audioContext);
	}
}

export class LocalVideoTrack extends LocalTrack {
	codec?: VideoCodec;

	constructor(mediaTrack: MediaStreamTrack, constraints?: MediaTrackConstraints, userProvided = true) {
		super(mediaTrack, Track.Kind.Video, constraints, userProvided);
		this.source = Track.Source.Camera;
	}

	/** WebRTC sender tuning; the MoQ encoder picks its own rate control. */
	async setDegradationPreference(preference: RTCDegradationPreference): Promise<void> {
		this.degradationPreference = preference;
	}

	degradationPreference?: RTCDegradationPreference;
}

/** Internal controls the MoQ subscriber uses on remote tracks. */
export interface RemoteTrackControl {
	setVolume(volume: number): void;
	getRTCStatsReport(): Promise<RTCStatsReport | undefined>;
	/** Plays in the shared mixing context rather than through <audio>. */
	isMixed?(): boolean;
	/** Output volume and mute (deafen) for mixed audio. */
	setOutput?(volume: number, muted: boolean): void;
}

export abstract class RemoteTrack extends Track {
	readonly receiver: RTCRtpReceiver | undefined = undefined;
	protected control?: RemoteTrackControl;

	protected constructor(mediaTrack: MediaStreamTrack, kind: Track.Kind, sid: string, control?: RemoteTrackControl) {
		super(mediaTrack, kind);
		this.sid = sid;
		this.control = control;
	}

	get isLocal(): false {
		return false;
	}

	/** @internal */
	setMuted(muted: boolean): void {
		this.setMutedState(muted);
	}

	/** @internal Swap in a new underlying track (the publisher restarted). */
	setMediaStreamTrack(track: MediaStreamTrack): void {
		this._mediaStreamTrack = track;
		this.rebindElements();
	}

	async getRTCStatsReport(): Promise<RTCStatsReport | undefined> {
		return this.control?.getRTCStatsReport();
	}

	start(): void {}
}

export class RemoteAudioTrack extends RemoteTrack {
	#volume = 1;
	#sinkId?: string;

	constructor(mediaTrack: MediaStreamTrack, sid: string, control?: RemoteTrackControl) {
		super(mediaTrack, Track.Kind.Audio, sid, control);
	}

	/** Volume 0..n; values above 1 amplify (gain happens before the element). */
	setVolume(volume: number): void {
		this.#volume = volume;
		this.control?.setVolume(volume);
	}

	getVolume(): number {
		return this.#volume;
	}

	/**
	 * parley: the audio plays in the shared mixing context (webAudioMix),
	 * so an attached <audio> element stays muted - it would play it twice -
	 * and output volume and deafen go through setOutput.
	 */
	get webAudioMixed(): boolean {
		return this.control?.isMixed?.() ?? false;
	}

	/** Output volume (0..1) and mute of mixed audio; see webAudioMixed. */
	setOutput(volume: number, muted: boolean): void {
		this.control?.setOutput?.(volume, muted);
	}

	async setSinkId(deviceId: string): Promise<void> {
		this.#sinkId = deviceId;
		await Promise.all(
			this.attachedElements.map(async (el) => {
				const withSink = el as HTMLMediaElement & {setSinkId?: (id: string) => Promise<void>};
				await withSink.setSinkId?.(deviceId);
			}),
		);
	}

	protected override bindElement(el: HTMLMediaElement): void {
		super.bindElement(el);
		if (this.webAudioMixed) el.muted = true;
		if (this.#sinkId) {
			const withSink = el as HTMLMediaElement & {setSinkId?: (id: string) => Promise<void>};
			void withSink.setSinkId?.(this.#sinkId).catch(() => {});
		}
	}
}

export class RemoteVideoTrack extends RemoteTrack {
	#dims?: Track.Dimensions;

	constructor(mediaTrack: MediaStreamTrack, sid: string, control?: RemoteTrackControl) {
		super(mediaTrack, Track.Kind.Video, sid, control);
	}

	override get dimensions(): Track.Dimensions | undefined {
		return this.#dims ?? super.dimensions;
	}

	/** @internal */
	setDimensions(dims: Track.Dimensions): void {
		if (this.#dims?.width === dims.width && this.#dims?.height === dims.height) return;
		this.#dims = dims;
		this.emit(TrackEvent.VideoDimensionsChanged, dims, this);
	}
}
